import type { PercentCrop } from 'react-image-crop';
import { centerCrop, makeAspectCrop } from 'react-image-crop';

// File validation
const DEFAULT_VALID_TYPES = ['image/jpeg', 'image/png'];
const DEFAULT_MAX_SIZE_MB = 5;

export function checkContentType(
  type: string,
  validTypes: string[] = DEFAULT_VALID_TYPES,
): boolean {
  return validTypes.includes(type);
}

export function checkFileSize(
  sizeBytes: number,
  maxSizeMB: number = DEFAULT_MAX_SIZE_MB,
): boolean {
  return sizeBytes <= maxSizeMB * 1024 * 1024;
}

export function validateFile(
  file: File,
  options?: {
    validTypes?: string[];
    maxSizeMB?: number;
  },
): { valid: boolean; error?: string } {
  if (!checkContentType(file.type, options?.validTypes)) {
    return {
      valid: false,
      error: `Invalid file type. Accepted: ${options?.validTypes?.join(', ') || DEFAULT_VALID_TYPES.join(', ')}`,
    };
  }

  if (!checkFileSize(file.size, options?.maxSizeMB)) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${options?.maxSizeMB || DEFAULT_MAX_SIZE_MB}MB`,
    };
  }

  return { valid: true };
}

// Crop helpers
export function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number,
): PercentCrop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

// Blob helpers
export async function getBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();

  // Ensure proper image MIME type
  if (blob.type.startsWith('image/')) {
    return blob;
  }
  return new Blob([blob], { type: 'image/png' });
}
